"use client";

import { useQuery } from "@apollo/client/react";
import { Route } from "lucide-react";
import { GetEnrollmentsForContactDocument } from "@/src/gql/graphql";
import { useSequencesAccess } from "@/lib/features/use-sequences-access";

/**
 * "On sequence — step N/M" badge for a contact. Self-gated to the rollout
 * allowlist (renders null otherwise), so it can be dropped into any contact
 * surface without muddying the UI for other users. Shows the most recent
 * live enrollment (active / paused / replied).
 */
export function SequenceStatusBadge({ contactId }: { contactId: string }) {
  const canSee = useSequencesAccess();
  const { data } = useQuery(GetEnrollmentsForContactDocument, {
    variables: { contactId },
    skip: !canSee || !contactId,
    fetchPolicy: "cache-and-network",
  });

  if (!canSee) return null;

  const enrollments = (data?.sales_sequence_enrollmentsCollection?.edges ?? []).map(
    (e) => e.node,
  );
  const live = enrollments.find(
    (e) => e.status === "active" || e.status === "paused" || e.status === "replied",
  );
  if (!live) return null;

  const total =
    live.sales_sequences?.sales_sequence_stepsCollection?.edges?.length ?? 0;
  const name = live.sales_sequences?.name ?? "Sequence";
  const paused = live.status === "paused" || live.status === "replied";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        paused
          ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
          : "bg-[var(--primary)]/10 text-[var(--primary)] ring-1 ring-[var(--primary)]/20"
      }`}
      title={paused ? "Cadence paused — they replied or were paused" : "Active outreach cadence"}
    >
      <Route className="h-3 w-3" />
      {paused ? "Sequence paused" : "On sequence"}: {name} · step{" "}
      {live.current_step_number}
      {total ? `/${total}` : ""}
    </span>
  );
}
